// Free-standing presentation mount at the pump corner. No fabricated dimensions,
// mechanical load rating or electrical installation approval is implied.
export const controllerStandPose = Object.freeze({
  pivot: Object.freeze([-5, 0, 3.6]), yaw: Math.PI / 2,
  controllerCenter: Object.freeze([-5.65, 4.75, 3.6]),
  controllerSupplyCenter: Object.freeze([-4.75, 3.65, 3.6]),
  chaacSupplyCenter: Object.freeze([-4.75, 2.25, 3.6]),
  couplingCenter: Object.freeze([-4.65, 4.08, 3.0]),
  serviceLane: Object.freeze([-4.65, 0, 2.9]),
  chaacServiceLane: Object.freeze([-4.8, 0, 3.15]),
  floorLevel: -1.55,
  postTop: 5.6
});

export function createControllerStandAssembly(THREE, { sceneRoot, controller, controllerPowerSupply, chaacPowerSupply }) {
  sceneRoot.updateMatrixWorld(true);
  const localInput = controller.worldToLocal(controllerPowerSupply.inputEndpoint.clone());
  const localTerminals = controllerPowerSupply.terminalCurves.map(curve => controller.worldToLocal(controllerPowerSupply.root.localToWorld(curve.getPoint(1))));
  const root = new THREE.Group(); root.name = "controller-and-power-supplies-common-stand";
  root.position.set(...controllerStandPose.pivot); root.rotation.y = controllerStandPose.yaw;
  root.userData = { schematic: true, role: "shared-controller-psu-mount", yaw: controllerStandPose.yaw, mounting: "free-standing-pump-corner", mechanicalRating: "not-validated" };
  sceneRoot.add(root); sceneRoot.updateMatrixWorld(true);
  // The stationary Chaac tail is separate from its relocated closed power brick.
  sceneRoot.attach(chaacPowerSupply.cable);
  root.add(controller, controllerPowerSupply.root, chaacPowerSupply.root);
  controller.position.copy(root.worldToLocal(new THREE.Vector3(...controllerStandPose.controllerCenter)));
  controller.rotation.set(0, 0, 0);
  const moveSupply = (supply, at) => {
    supply.root.rotation.set(0, 0, 0);
    supply.root.position.copy(root.worldToLocal(new THREE.Vector3(...at))).sub(supply.supply.position);
  };
  moveSupply(controllerPowerSupply, controllerStandPose.controllerSupplyCenter);
  moveSupply(chaacPowerSupply, controllerStandPose.chaacSupplyCenter);
  sceneRoot.updateMatrixWorld(true);

  const fixedFrameBrackets = [], fixedFrameClamps = [];
  const bracketNames = new Set(["controller-psu-left-frame-bracket", "chaac-psu-frame-bracket-lower", "chaac-psu-frame-bracket-upper"]);
  const clampNames = new Set(["controller-psu-frame-clamp", "chaac-psu-left-frame-clamp"]);
  const pivot = new THREE.Vector3(...controllerStandPose.pivot);
  root.traverse(object => {
    if (clampNames.has(object.name)) {
      const at = object.getWorldPosition(new THREE.Vector3()); at.x = pivot.x; at.z = pivot.z;
      object.position.copy(object.parent.worldToLocal(at)); fixedFrameClamps.push(object);
    }
    if (!bracketNames.has(object.name)) return;
    const height = object.geometry.parameters.height;
    const ends = [-1, 1].map(sign => object.localToWorld(new THREE.Vector3(0, sign * height / 2, 0)));
    const supply = object.name.startsWith("chaac") ? chaacPowerSupply : controllerPowerSupply;
    const plate = supply.mountingPlate.getWorldPosition(new THREE.Vector3());
    const supplyAnchor = ends[ends[0].distanceTo(plate) < ends[1].distanceTo(plate) ? 0 : 1].clone();
    const postAnchor = new THREE.Vector3(pivot.x, supplyAnchor.y, pivot.z);
    const a = object.parent.worldToLocal(supplyAnchor.clone()), b = object.parent.worldToLocal(postAnchor.clone());
    object.position.copy(a).add(b).multiplyScalar(.5);
    object.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
    object.scale.y = a.distanceTo(b) / height;
    fixedFrameBrackets.push({ object, postAnchor, supplyAnchor });
  });

  const geometries = new Set(), materials = new Set();
  const surface = (color, options = {}) => {
    const material = new THREE.MeshStandardMaterial({ color, roughness: .43, metalness: .6, ...options }); materials.add(material); return material;
  };
  const metal = surface(0x9ea9a7), dark = surface(0x303a39, { roughness: .64, metalness: .3 });
  const channelMaterial = surface(0x5c6662, { roughness: .7, metalness: .04 });
  const mesh = (name, geometry, material, at) => {
    geometries.add(geometry); const object = new THREE.Mesh(geometry, material); object.name = name;
    object.position.set(...at); object.castShadow = true; object.receiveShadow = true; object.userData = { schematic: true, standHardware: true }; root.add(object); return object;
  };
  const box = (name, size, material, at) => mesh(name, new THREE.BoxGeometry(...size), material, at);
  const { floorLevel, postTop } = controllerStandPose;
  const pole = mesh("controller-pump-corner-mounting-post", new THREE.CylinderGeometry(.052, .052, postTop - floorLevel, 16), metal, [0, (postTop + floorLevel) / 2, 0]);
  const base = box("controller-stand-floor-base", [.85, .1, .65], dark, [0, floorLevel + .05, 0]);
  const feet = [-1, 1].flatMap(x => [-1, 1].map(z => mesh("controller-stand-floor-fastener", new THREE.CylinderGeometry(.027, .027, .025, 12), metal, [x * .32, floorLevel + .1125, z * .22])));
  const controllerMount = box("controller-rear-mounting-plate", [1.05, .6, .055], metal, [0, 4.75, -.18]);
  [-.22, .22].forEach(y => box("controller-short-mounting-bridge", [.14, .10, .15], metal, [0, 4.75 + y, -.075]));
  const serviceAt = root.worldToLocal(new THREE.Vector3(...controllerStandPose.serviceLane));
  const channelBottom = floorLevel + .15, channelTop = 4.30;
  const cableChannel = box("controller-stand-closed-cable-channel", [.14, channelTop - channelBottom, .12], channelMaterial, [serviceAt.x, (channelTop + channelBottom) / 2, serviceAt.z]);
  const chaacServiceAt = root.worldToLocal(new THREE.Vector3(...controllerStandPose.chaacServiceLane));
  const chaacChannelTop = 2.93;
  const chaacCableChannel = box("chaac-stand-separate-closed-cable-channel", [.08, chaacChannelTop - channelBottom, .08], channelMaterial, [chaacServiceAt.x, (chaacChannelTop + channelBottom) / 2, chaacServiceAt.z]);
  [floorLevel + .35, 2.9, 4.05].forEach(y => {
    const a = new THREE.Vector3(0, y, 0), b = new THREE.Vector3(serviceAt.x, y, serviceAt.z);
    const arm = mesh("controller-cable-channel-support", new THREE.CylinderGeometry(.018, .018, a.distanceTo(b), 8), metal, a.clone().add(b).multiplyScalar(.5).toArray());
    arm.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.sub(a).normalize());
  });
  sceneRoot.updateMatrixWorld(true);
  const replacedCableGeometry = chaacPowerSupply.rerouteMountedSupply({ serviceLane: new THREE.Vector3(...controllerStandPose.chaacServiceLane) });
  const rebindPowerEndpoints = ({ endpoint, terminals } = {}) => controllerPowerSupply.rerouteMountedSupply({
    endpoint: endpoint || controller.localToWorld(localInput.clone()),
    terminals: terminals || localTerminals.map(point => controller.localToWorld(point.clone())),
    couplingPosition: new THREE.Vector3(...controllerStandPose.couplingCenter)
  });
  const replacedGeometries = [replacedCableGeometry, ...rebindPowerEndpoints()];
  let disposed = false;
  return {
    root, pole, base, feet, controllerMount, cableChannel, chaacCableChannel, replacedCableGeometry, replacedGeometries, fixedFrameBrackets, fixedFrameClamps, pivot, yaw: controllerStandPose.yaw,
    serviceLane: new THREE.Vector3(...controllerStandPose.serviceLane), rebindPowerEndpoints,
    dispose() { if (disposed) return; disposed = true; geometries.forEach(item => item.dispose()); materials.forEach(item => item.dispose()); }
  };
}

export function controllerFocusTheta(THREE, boardRoot, side) {
  boardRoot.updateWorldMatrix(true, false);
  const facing = new THREE.Vector3(0, 0, side === "display" ? -1 : 1).transformDirection(boardRoot.matrixWorld);
  return Math.atan2(facing.x, facing.z) + (side === "display" ? 0 : .12);
}
